import { useQuery } from '@tanstack/react-query'

import { PagedDataTable } from '@/components/data-table/PagedDataTable'
import { DateRangePicker } from '@/components/filters/DateRangePicker'
import { FilterBar, FilterField } from '@/components/filters/FilterBar'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { AuditDetailPanel } from '@/features/admin/AuditDetailPanel'
import type { LegacyColumnDef as ColumnDef } from '@tanstack/react-table/legacy'
import { useQueryState } from '@/hooks/use-query-state'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import { AUDIT_ACTIONS } from '@/lib/constants'
import type { AuditLog } from '@/types/models'

const PAGE_SIZE = 25
const ACTOR_PAGE_SIZE = 200

const actionLabel = (action: string) => messages.audit.actions[action] ?? action

const targetText = (log: AuditLog) =>
  log.target_type ? `${log.target_type}:${log.target_id ?? ''}` : messages.common.empty

// Sunucu sıralaması yok: tüm sütunlar `enableSorting: false`.
const columns: ColumnDef<AuditLog>[] = [
  {
    id: 'occurred_at',
    header: messages.audit.columns.occurred_at,
    accessorFn: (log) => formatDateTime(log.occurred_at),
    enableSorting: false,
    size: 180,
  },
  {
    id: 'actor_email',
    header: messages.audit.columns.actor_email,
    accessorFn: (log) => log.actor_email ?? messages.common.unknown,
    enableSorting: false,
    size: 240,
  },
  {
    id: 'action',
    header: messages.audit.columns.action,
    accessorFn: (log) => actionLabel(log.action),
    enableSorting: false,
    size: 220,
    cell: ({ row }) => <span title={row.original.action}>{actionLabel(row.original.action)}</span>,
  },
  {
    id: 'target',
    header: messages.audit.columns.target,
    accessorFn: targetText,
    enableSorting: false,
    size: 300,
  },
  {
    id: 'ip',
    header: messages.audit.columns.ip,
    accessorFn: (log) => log.ip ?? messages.common.empty,
    enableSorting: false,
    size: 140,
  },
]

export function AuditPage() {
  const { get, set, reset } = useQueryState()
  const dateFrom = get('date_from')
  const dateTo = get('date_to')
  const action = get('action')
  const actorId = get('actor_id')
  const page = Number(get('page') ?? '1')
  // Ayrıntı paneli: satır mevcut sayfanın verisinden bulunur (tek kayıt ucu yok).
  const selectedLogId = get('kayit')

  const query = {
    date_from: dateFrom,
    date_to: dateTo,
    action,
    actor_id: actorId,
    page,
    page_size: PAGE_SIZE,
  }

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.admin.auditLogs(query),
    queryFn: () => services.admin.listAuditLogs(query),
  })
  // Kişi seçenekleri: kullanıcı kataloğu küçük, sözleşmenin üst sınırıyla tek sayfa.
  const usersQuery = useQuery({
    queryKey: queryKeys.admin.users({ page: 1, page_size: ACTOR_PAGE_SIZE }),
    queryFn: () => services.admin.listUsers({ page: 1, page_size: ACTOR_PAGE_SIZE }),
  })
  const actorOptions = [...(usersQuery.data?.items ?? [])]
    .sort((a, b) => a.full_name.localeCompare(b.full_name, 'tr'))
    .map((user) => ({ value: user.id, label: `${user.full_name} (${user.email})` }))
  const selectedLog = data?.items.find((log) => log.id === selectedLogId) ?? null

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold">{messages.audit.title}</h1>
        <p className="text-muted-foreground text-sm">{messages.audit.description}</p>
      </div>

      <FilterBar onReset={() => reset(['date_from', 'date_to', 'action', 'actor_id', 'page'])}>
        <DateRangePicker
          idPrefix="audit"
          from={dateFrom}
          to={dateTo}
          onChange={({ from, to }) => set({ date_from: from, date_to: to, page: undefined })}
        />
        <FilterField label={messages.audit.filters.actor} htmlFor="audit-actor">
          <OptionSelect
            id="audit-actor"
            className="w-64"
            value={actorId ?? ''}
            options={[{ value: '', label: messages.common.all }, ...actorOptions]}
            onValueChange={(next) => set({ actor_id: next || undefined, page: undefined })}
          />
        </FilterField>
        <FilterField label={messages.audit.filters.action} htmlFor="audit-action">
          <OptionSelect
            id="audit-action"
            className="w-56"
            value={action ?? ''}
            options={[
              { value: '', label: messages.common.all },
              ...AUDIT_ACTIONS.map((item) => ({ value: item, label: actionLabel(item) })),
            ]}
            onValueChange={(next) => set({ action: next || undefined, page: undefined })}
          />
        </FilterField>
      </FilterBar>

      {isLoading ? (
        <Loading rows={8} />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.items.length === 0 ? (
        <Empty />
      ) : (
        <PagedDataTable<AuditLog>
          tableId="admin-audit"
          columns={columns}
          data={data.items}
          total={data.total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={(next) => set({ page: String(next), kayit: undefined })}
          onRowClick={(log) => set({ kayit: log.id })}
          exportFilename="denetim-kaydi"
        />
      )}

      <AuditDetailPanel log={selectedLog} onClose={() => set({ kayit: undefined })} />
    </div>
  )
}
